import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { SunkenPanel } from '@kerfjs/ui/sunken-panel';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';

import { CLARIFY_FORM_REASON } from '../../../shared/contract/forms.js';
import { type FormRow, questionsDialogOpen, questionsDraft } from '../../data/state.js';
import { FormField } from './form-field.js';

/** Dialog for answering, saving, or skipping the demand's next open question form. */
export function QuestionsDialog({
  openForms,
  lightDismiss = false,
}: {
  openForms: FormRow[];
  lightDismiss?: boolean;
}): SafeHtml {
  if (!questionsDialogOpen.value) return <></>;
  const form = openForms[0];
  if (form === undefined) {
    return (
      <DialogSurface
        label="Questions"
        className="questions-dialog"
        lightDismiss={lightDismiss}
        rootAttributes={{ 'data-key': 'questions-empty', 'data-auto-open-dialog': '' }}
        footer={
          <Button
            tone="neutral"
            size="default"
            emphasis="plain"
            action="questions-dialog-close"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Close
          </Button>
        }
      >
        <Text tone="quiet" size="compact">
          All caught up — no questions are waiting.
        </Text>
      </DialogSurface>
    );
  }
  const spec = form.schema;
  // The pricing check's optional questions already ran as assumptions: answering re-checks (FR-3.12).
  const recheck = form.reason === CLARIFY_FORM_REASON;
  const draft = questionsDraft.value?.formId === form.id ? questionsDraft.value.answers : undefined;
  return (
    <DialogSurface
      label={spec.title}
      className="questions-dialog"
      lightDismiss={lightDismiss}
      rootAttributes={{ 'data-key': form.id, 'data-unsaved-guard': 'your answers', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            tone="neutral"
            size="default"
            className="questions-dialog-cancel"
            emphasis="plain"
            action="questions-dialog-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          {spec.allowSkip === true && form.status === 'open' ? (
            <Button tone="neutral" size="default" emphasis="plain" rootAttributes={{ 'data-form-skip': form.id }}>
              Skip
            </Button>
          ) : null}
          <Spacer flex />
          <Button
            tone="neutral"
            size="default"
            emphasis="secondary"
            rootAttributes={{ 'data-form-save-draft': form.id, 'data-unsaved-save': '' }}
          >
            Save Draft
          </Button>
          <Button size="default" tone="brand" emphasis="primary" rootAttributes={{ 'data-form-answer': form.id }}>
            {recheck ? 'Answer and Re-check' : 'Submit'}
          </Button>
        </>
      }
    >
      {/* The data-key (a morph key) makes each form a fresh subtree, so answers never leak between forms. */}
      <List gap="m" rootAttributes={{ 'data-key': form.id }}>
        {spec.intro !== undefined ? (
          <ListInsetControl sides="rl">
            <Text tone="quiet" size="compact">
              {spec.intro}
            </Text>
          </ListInsetControl>
        ) : null}
        <List gap="l">
          {spec.fields.map((field, index) => (
            <List rootAttributes={{ 'data-key': field.key }}>
              <ListHeader label={`Question ${String(index + 1)}`} />
              <SunkenPanel>
                <ListInsetControl>
                  <FormField formId={form.id} field={field} draft={draft} />
                </ListInsetControl>
              </SunkenPanel>
            </List>
          ))}
        </List>
      </List>
    </DialogSurface>
  );
}
